/**
 * Which tools an administrator has switched off for standard users (settings/toolPermissions).
 *
 * Read each time a tool's page is opened, so a switch made in Settings is felt the next time
 * a standard user opens or reloads a tool. Not read at all for an administrator: the switch
 * never applies to one, and a read that is not made cannot hang.
 *
 * `status`:
 *   "skipped"  not asked for (an administrator, or nobody signed in yet)
 *   "loading"  the first read in this tab is on its way: the page must not be drawn yet
 *   "ready"    `tools` is the map: { [toolId]: false } means switched off
 *   "failed"   the read did not come back (timed out, or refused) and nothing is remembered
 *
 * The last answer is remembered for the tab. Moving from one tool to another therefore draws
 * at once from that answer while a fresh read is made behind it; if the fresh answer differs,
 * the page follows it a moment later. Without this a standard user would watch a "checking"
 * spinner on every move between tools.
 *
 * ⚠️ On "failed" the page opens, as the menu does when the same read fails (it falls back to
 * every tool the person's role allows). The switch is not what keeps data safe: the server
 * answers any colleague either way. Refusing every tool whenever one small read stalls would
 * shut standard users out of the whole site for nothing. A read that fails AFTER one has
 * worked keeps the remembered answer, so a switched-off tool does not reopen on a bad
 * connection.
 */
import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { withTimeout } from "../utils/withTimeout";

const SKIPPED = { status: "skipped", tools: null };
const LOADING = { status: "loading", tools: null };

let remembered = null; // the last map read in this tab, or null

/** For tests, and for anything that must start from nothing. */
export function forgetToolSwitches() {
  remembered = null;
}

const startingState = (enabled) => {
  if (!enabled) return SKIPPED;
  return remembered ? { status: "ready", tools: remembered } : LOADING;
};

export function useToolSwitches(enabled) {
  const [state, setState] = useState(() => startingState(enabled));

  useEffect(() => {
    setState(startingState(enabled));
    if (!enabled) return undefined;
    let cancelled = false;
    withTimeout(getDoc(doc(db, "settings", "toolPermissions")), 8000)
      .then((snapshot) => {
        const data = snapshot.exists() ? snapshot.data() : null;
        remembered = (data && data.tools) || {};
        if (!cancelled) setState({ status: "ready", tools: remembered });
      })
      .catch((error) => {
        console.error("Could not read which tools are switched off:", error);
        if (!cancelled && !remembered) setState({ status: "failed", tools: null });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  // The first render after `enabled` turns on still holds the old state; never let that
  // render say "skipped", or the page would be drawn before the switch has been read.
  if (enabled && state.status === "skipped") return startingState(true);
  return state;
}

export default useToolSwitches;
